import { useMemo } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { makeStyles, tokens, Text } from '@coreai/fluentui-react';
import { ChevronRight16Regular } from '@fluentui/react-icons';
import { useAppWorkflows } from '@/hooks/useAppResource';
import { useAppPermission } from '@/hooks/useAppPermission';
import { useProjectsMultiRegion, useProviderLocations } from '@/hooks/useAzureResources';
import { useAppsInProject } from '@/hooks/useApps';
import { useIsProjectOwner, usePermission } from '@/hooks/useUserPermissions';
import { useTelemetry } from '@/hooks/useTelemetry';
import { paths } from '@/routes';
import { CREATE_FLAG_PARAM } from '@/routes/queryParams';
import { openCreateWorkflowDialog } from '@/stores/createWorkflowDialogStore';
import { WorkspacePill } from './WorkspacePill';
import { AppPill } from './AppPill';
import { WorkflowLeafPill, type WorkflowStatus } from './WorkflowLeafPill';
import type { AutomationAppUIModel } from '@/types/automation';
import { APP_SHELL_MOBILE_QUERY } from '@/theme/appShell';

const useStyles = makeStyles({
  nav: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    minWidth: 0,
    flexShrink: 1,
    [`@media ${APP_SHELL_MOBILE_QUERY}`]: {
      display: 'none',
    },
  },
  separator: {
    color: tokens.colorNeutralForeground4,
    flexShrink: 0,
    // Fluent icons size to 1em, so they inherit the surrounding font-size.
    // Pin to 16px so the chevron renders at a strict 16px regardless of the
    // ambient typography of the header.
    fontSize: tokens.fontSizeBase400,
  },
  preProjectLink: {
    color: tokens.colorNeutralForeground2,
    textDecoration: 'none',
    fontSize: tokens.fontSizeBase300,
    paddingInline: tokens.spacingHorizontalXS,
    paddingBlock: tokens.spacingVerticalXXS,
    borderRadius: tokens.borderRadiusSmall,
    ':hover': {
      color: tokens.colorNeutralForeground1,
      backgroundColor: tokens.colorSubtleBackgroundHover,
    },
    ':focus-visible': {
      outlineWidth: '2px',
      outlineStyle: 'solid',
      outlineColor: tokens.colorStrokeFocus2,
      outlineOffset: '2px',
    },
  },
  preProjectCurrent: {
    color: tokens.colorNeutralForeground1,
    fontSize: tokens.fontSizeBase300,
    fontWeight: tokens.fontWeightSemibold,
    paddingInline: tokens.spacingHorizontalXS,
  },
});

interface HeaderNavigationProps {
  /**
   * Optional override for the leaf workflow's status. In production this would
   * come from a runtime status hook; for now the caller can supply it (or
   * leave undefined to render the leaf without a status dot).
   */
  workflowStatus?: WorkflowStatus;
}

/**
 * Pill-based hierarchy navigation for the global header.
 *
 * Renders progressively from URL params:
 *  - location only           → simple "Region · Projects" link path
 *  - location + project      → WorkspacePill (single combined chip)
 *  - + app                   → WorkspacePill + AppPill
 *  - + workflow              → WorkspacePill + AppPill + WorkflowLeafPill
 *
 * Replaces the legacy NavigationBreadcrumbs component. URL navigation contracts
 * are preserved: switching project lands on its apps list, switching app on
 * its workflows list, switching workflow on the workflow designer.
 */
export const HeaderNavigation = ({ workflowStatus }: HeaderNavigationProps = {}) => {
  const styles = useStyles();
  const location = useLocation();
  const params = useParams<{
    tenantId?: string;
    locationName?: string;
    projectName?: string;
    appName?: string;
    workflowName?: string;
  }>();
  const { tenantId, locationName, projectName, appName, workflowName } = params;

  const { data: regions } = useProviderLocations();
  // Fan out across every region so the region submenu shows accurate
  // per-region project counts. Single-region `useProjects()` would leave
  // every non-current region rendering "No projects".
  const { projects } = useProjectsMultiRegion();
  const { data: apps } = useAppsInProject(projectName ?? '');

  // Build a URL-driven app context for workflow fetching. Synthesizing this
  // from URL params (rather than reading `selectedApp` from the Zustand store)
  // closes the race window between `navigate()` updating the URL and the
  // layout hydrating the store, which would otherwise leave the leaf pill
  // showing workflows from the previously-selected app. `useAppResource`
  // only reads `app.name` and `app.projectName`, so a minimal shape is enough.
  const urlApp = useMemo(
    () =>
      appName && projectName
        ? ({ name: appName, projectName } as AutomationAppUIModel)
        : null,
    [appName, projectName],
  );

  const { data: workflows } = useAppWorkflows(urlApp);

  const workflowSummaries = useMemo(
    () => workflows?.map((w) => ({ name: w.name })) ?? [],
    [workflows],
  );

  const { trackEvent } = useTelemetry();
  const navigate = useNavigate();

  // ── Create permissions
  //
  // Project: not gated client-side — DashboardHomePage's "Create project"
  // flow opens an external Azure portal link, and we mirror that behaviour
  // by always offering the affordance.
  //
  // App: project Owner OR a Contributor/Author on the project.
  // Workflow: app Contributor or Owner (canEdit on the current app).
  const { data: isProjectAdmin } = useIsProjectOwner(projectName ?? '');
  const { data: projectContributor } = usePermission(
    projectName ?? '',
    undefined,
    ['Contributor', 'Author'],
  );
  const canCreateApp = Boolean(isProjectAdmin) || Boolean(projectContributor);
  const { canEdit: canCreateWorkflow } = useAppPermission(urlApp ?? undefined, { enabled: !!urlApp });

  // ── Create handlers
  //
  // Project + App: navigate to the relevant list page with `?create=1` so the
  // destination page opens its existing Create dialog on mount. Keeps the
  // dialog wiring inside the list pages.
  //
  // Workflow: open the global CreateWorkflowDialog in place via the dialog
  // store. The host (`CreateWorkflowDialogHost`) is mounted in `AppLayout`,
  // so we're guaranteed to have app context here. This avoids the previous
  // navigate-to-list-and-redirect-back bounce when triggered from inside
  // the designer.
  const handleCreateProject = () => {
    const path = paths.locationProjects(locationName ?? '', tenantId);
    const sep = path.includes('?') ? '&' : '?';
    navigate(`${path}${sep}${CREATE_FLAG_PARAM}=1`);
  };
  const handleCreateApp = () => {
    if (!projectName || !locationName) return;
    navigate(`${paths.projectApps(locationName, projectName, tenantId)}?${CREATE_FLAG_PARAM}=1`);
  };
  const handleCreateWorkflow = () => {
    if (!projectName || !locationName || !appName) return;
    openCreateWorkflowDialog({ source: 'header' });
  };

  // ── Pre-project state: just region, optionally followed by a "Projects" current-page label
  if (!projectName) {
    if (!locationName) return null;
    const regionLabel = regions?.find((r) => r.name === locationName)?.displayName ?? locationName;
    // Projects is the default tab on the home + location-scoped dashboard
    // routes (/, /location/:locationName, /tenant/:tenantId/location/:locationName).
    // The Recent tab opts in via ?tab=recent, so anything that isn't an
    // explicit Recent selection is the Projects view. Without this the
    // breadcrumb pill renders as a Link on location-scoped routes and AT
    // doesn't get the correct aria-current="page".
    const onProjects = !location.search.includes('tab=recent');
    return (
      <nav aria-label="Breadcrumb" className={styles.nav}>
        <Link to={paths.locationProjects(locationName, tenantId)} className={styles.preProjectLink}>
          {regionLabel}
        </Link>
        <ChevronRight16Regular className={styles.separator} aria-hidden="true" />
        {onProjects ? (
          <Text className={styles.preProjectCurrent} aria-current="page">
            Environments
          </Text>
        ) : (
          <Link to={paths.locationProjects(locationName, tenantId)} className={styles.preProjectLink}>
            Environments
          </Link>
        )}
      </nav>
    );
  }

  // ── Workspace pill is mandatory once we're inside a project
  return (
    <nav aria-label="Breadcrumb" className={styles.nav}>
      <WorkspacePill
        locationName={locationName ?? ''}
        projectName={projectName}
        projects={projects}
        tenantId={tenantId}
        isAtProjectRoot={!appName}
        onCreate={handleCreateProject}
        onProjectChange={(name) => trackEvent('navigation.project.changed', { project: name })}
      />
      {appName && (
        <>
          <ChevronRight16Regular className={styles.separator} aria-hidden="true" />
          <AppPill
            locationName={locationName ?? ''}
            projectName={projectName}
            appName={appName}
            apps={apps ?? []}
            tenantId={tenantId}
            isAtAppRoot={!workflowName}
            canCreate={canCreateApp}
            onCreate={handleCreateApp}
            onAppChange={(name) => trackEvent('navigation.app.changed', { app: name })}
          />
        </>
      )}
      {appName && workflowName && (
        <>
          <ChevronRight16Regular className={styles.separator} aria-hidden="true" />
          <WorkflowLeafPill
            locationName={locationName ?? ''}
            projectName={projectName}
            appName={appName}
            workflowName={workflowName}
            workflows={workflowSummaries}
            currentStatus={workflowStatus}
            tenantId={tenantId}
            canCreate={canCreateWorkflow}
            onCreate={handleCreateWorkflow}
            onWorkflowChange={(name) => trackEvent('navigation.workflow.changed', { workflow: name })}
          />
        </>
      )}
    </nav>
  );
};
